import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type JourneyHealthResult,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { HelpTip } from '../components/HelpTip';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { boundaryPhrase, isHighImpact, journeyTypeLabel, testedBoundaryPhrase } from './journeyLabels';
import styles from './JourneyHealth.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface JourneyHealthProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * Project journey-health view (#40a discovery axis + #40b execution axis). Lists
 * every discovered journey type with its discovery-time status and, when a real
 * terminal run backs it, the execution-derived outcome of its latest run
 * (validated / failed / flaky / …). The two axes are shown as SEPARATE badges: an
 * execution headline (only when a run exists) beside the discovery status. A type
 * with no in-window run shows the discovery badge alone — the no-overclaim
 * guarantee (an execution state appears ONLY when a real run produced it). A
 * manual Refresh re-fetches (no polling); async results announce in a polite live
 * region.
 */
export function JourneyHealth({ client = defaultClient }: JourneyHealthProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [data, setData] = useState<JourneyHealthResult | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const next = await client.journeyHealth(orgId, id);
      setData(next);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || refreshing) return;
    setRefreshing(true);
    try {
      const next = await client.journeyHealth(orgId, id);
      setData(next);
      setStatusMessage('Journey health updated.');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, refreshing]);

  // Trigger execution of the RUNNABLE journeys. Only customer-provided journeys
  // have attested steps to run — the server rejects discovered ones — so we send
  // just those ids (capped at the API's 50-id limit). Requires journey:activate.
  const runJourneys = useCallback(async () => {
    if (!orgId || !id || running || !data) return;
    const ids = data.journeys
      .filter((e) => e.source === 'customer-provided' && e.journeyId)
      .map((e) => e.journeyId as string)
      .slice(0, 50);
    if (ids.length === 0) return;
    setRunning(true);
    setRunMessage(null);
    try {
      const result = await client.triggerJourneyRun(orgId, id, {
        selectionMode: 'group',
        journeyIds: ids,
      });
      const n = result.runIds.length;
      setRunMessage(
        `Started ${n} journey run${n === 1 ? '' : 's'}. Results and any potential defects appear once the runs finish.`,
      );
    } catch (err) {
      setRunMessage(toDisplayError(err).message);
    } finally {
      setRunning(false);
    }
  }, [client, orgId, id, running, data]);

  // The server returns rows in the #42 attention ranking (fold-owned); render as-is.
  const sorted = data ? data.journeys : [];
  const hasRows = sorted.length > 0;
  // journey:activate is org_admin + editor only; viewers/reviewers never see Run.
  const canActivate = currentOrg?.role === 'org_admin' || currentOrg?.role === 'editor';
  const runnableCount = sorted.filter(
    (e) => e.source === 'customer-provided' && e.journeyId,
  ).length;

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 id="jh-heading" className={styles.heading}>
          Journey health
        </h1>
        <p className={styles.subheading}>
          The customer journeys TestCraft found on your live storefront. Each one is safely mapped
          like a shopper would experience it &mdash; and checked once you connect test credentials.{' '}
          <HelpTip term="journey" />
        </p>
        <p className={styles.noVerdict}>
          This dashboard is informational. TestCraft reports production journey health and ranks
          what needs attention &mdash; it never issues a pass/fail or release go/no-go. You decide.
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading journey health…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && data ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              {canActivate && runnableCount > 0 ? (
                <Button variant="primary" busy={running} onClick={runJourneys}>
                  {running ? 'Starting runs…' : 'Run journeys'}
                </Button>
              ) : null}
              <Link to={`/projects/${id ?? ''}/test-cases`} className={styles.backLink}>
                View test cases
              </Link>
              <Link to={`/projects/${id ?? ''}/defects`} className={styles.backLink}>
                View defects
              </Link>
              <Link to={`/projects/${id ?? ''}/journeys`} className={styles.backLink}>
                Manage journeys
              </Link>
            </div>

            {runMessage ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">{runMessage}</InlineAlert>
              </div>
            ) : null}
            {canActivate && hasRows && runnableCount === 0 ? (
              <p className={styles.noVerdict}>
                Only customer-provided journeys can be run. The journeys above were discovered by a
                scan &mdash; import attested journeys for this project to validate them and capture
                any defects.
              </p>
            ) : null}

            {!data.hasCompletedScanInWindow ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">
                  No scans have completed yet — run a scan to discover journeys.{' '}
                  {hasRows ? 'Customer-provided journeys you added are shown below. ' : null}
                  <Link to={`/projects/${id ?? ''}/scans`}>Go to scans</Link>
                </InlineAlert>
              </div>
            ) : null}

            {data.hasCompletedScanInWindow && !hasRows ? (
              <div className={styles.panel}>
                <InlineAlert severity="warning">
                  No journeys detected in the last {data.windowDays} days.
                </InlineAlert>
              </div>
            ) : null}

            {hasRows ? (
              <>
                {sorted.some((entry) => entry.status === 'configuration_required') ? (
                  <div className={styles.panel}>
                    <InlineAlert severity="info">
                      <strong>What you&rsquo;re seeing.</strong> TestCraft discovered these journeys
                      and followed each one up to the point where a real shopper would sign in,
                      create an account, or pay. It stops there on purpose &mdash; it never
                      registers, logs in, or checks out on your live store without your say-so.
                      Connect test credentials for this site and TestCraft will validate each
                      journey end-to-end. Health results, pass/fail runs, and potential defects
                      appear after those validation runs &mdash; that&rsquo;s why this page shows
                      discovery only for now.
                    </InlineAlert>
                  </div>
                ) : null}
                <table className={styles.table} aria-labelledby="jh-heading">
                <thead>
                  <tr>
                    <th scope="col">Journey</th>
                    <th scope="col">Source</th>
                    <th scope="col">Status</th>
                    <th scope="col">Latest run</th>
                    <th scope="col">Latest scan</th>
                    <th scope="col">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((entry) => (
                    <tr key={`${entry.journeyType}-${entry.source}`}>
                      <td>
                        {entry.source === 'discovered' ? (
                          <Link to={`/projects/${id ?? ''}/journey-health/${entry.journeyType}`}>
                            {journeyTypeLabel(entry.journeyType)}
                          </Link>
                        ) : (
                          journeyTypeLabel(entry.journeyType)
                        )}
                        {isHighImpact(entry) ? (
                          <span className={styles.highImpact}>High impact</span>
                        ) : null}
                      </td>
                      <td>
                        <StatusBadge kind="source" status={entry.source} />
                      </td>
                      <td>
                        {entry.executionStatus ? (
                          <div className={styles.badges}>
                            <StatusBadge kind="execution" status={entry.executionStatus} />
                            <StatusBadge kind="journey" status={entry.status} />
                          </div>
                        ) : (
                          <StatusBadge kind="journey" status={entry.status} />
                        )}
                      </td>
                      <td>{formatTime(entry.latestRunAt) ?? '—'}</td>
                      <td>{formatTime(entry.latestScanAt) ?? 'Not scanned'}</td>
                      <td>
                        <div className={styles.boundary}>{testedBoundaryPhrase(entry)}</div>
                        {entry.status === 'configuration_required' ? (
                          <>
                            <p className={styles.boundaryNote}>
                              {`TestCraft pauses at ${boundaryPhrase(
                                entry.boundaryKind,
                              )} to avoid changing anything on your live store — add test credentials to check it end-to-end.`}
                            </p>
                            <div className={styles.detailLink}>
                              <Link
                                to={`/projects/${id ?? ''}/connected-validation`}
                                aria-label={`Add test credentials for ${journeyTypeLabel(entry.journeyType)} (${entry.source})`}
                              >
                                Add test credentials
                              </Link>
                            </div>
                          </>
                        ) : null}
                        {entry.journeyId ? (
                          <div className={styles.detailLink}>
                            <Link
                              to={`/projects/${id ?? ''}/journeys/${entry.journeyId}`}
                              aria-label={`View journey model for ${journeyTypeLabel(entry.journeyType)} (${entry.source})`}
                            >
                              View journey model
                            </Link>
                          </div>
                        ) : null}
                        {entry.executionStatus ? (
                          <div className={styles.detailLink}>
                            <Link
                              to={`/projects/${id ?? ''}/journey-health/${entry.journeyType}/execution?source=${entry.source}`}
                              aria-label={`View run evidence for ${journeyTypeLabel(entry.journeyType)} (${entry.source})`}
                            >
                              View run evidence
                            </Link>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : null}

            <div className={styles.blocked}>
              <p className={styles.blockedTitle}>Blocked / excluded areas: {data.blockedCount}</p>
              <p className={styles.blockedHelp}>
                Areas TestCraft intentionally skipped &mdash; off-limits by the site&rsquo;s robots
                rules or your crawl settings (e.g. admin/system pages).
              </p>
            </div>

            <div className={styles.note}>
              <InlineAlert severity="info">
                Each journey shows its discovery status and, once a validation run has completed,
                the outcome of its latest run. A journey with no completed run yet shows its
                discovery status only.
              </InlineAlert>
            </div>
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
